import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

import { Badge, Button, EmptyState, Select, Table, type TableColumn } from "../../../components/ui";
import { PurchaseDetailModal } from "../components/PurchaseDetailModal";
import { PurchaseFormModal } from "../components/PurchaseFormModal";
import { ErrorState, InlineSpinner, Loader, SectionFrame } from "../components/shared";
import { useCanSeeCosts, usePurchases, useSuppliers } from "../hooks/useActivos";
import { formatDate, formatMoney } from "../lib/format";
import { FINANCE_STATUS_TONES, financeStatusLabel, langOf, toneOf } from "../lib/vocab";
import type { PurchaseRow } from "../services/activosApi";

// ============================================================================
// Compras (antes «lotes»): una compra es una factura con sus piezas. Archivo
// común, idéntico en CC-CONSOLE y WAYAK. Con `feat:activos.costos` se ven el
// total y el estado de Finanzas (`pendiente` hasta el PR 4); sin la llave, ni
// columnas ni campos de costo.
// ============================================================================

const PARAM = "compra";

export default function ComprasSection() {
  const { t, i18n } = useTranslation("activos");
  const lang = langOf(i18n.language);
  const canSeeCosts = useCanSeeCosts();
  const [params, setParams] = useSearchParams();
  const [supplierId, setSupplierId] = useState("");
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<PurchaseRow | null>(null);
  const suppliers = useSuppliers({});
  const list = usePurchases(supplierId || undefined);
  const rows = useMemo(() => (list.data?.pages || []).flatMap((p) => p.items || []), [list.data]);
  const openId = params.get(PARAM);

  const open = (id: string | null) => {
    const next = new URLSearchParams(params);
    if (id) next.set(PARAM, id);
    else next.delete(PARAM);
    setParams(next, { replace: true });
  };
  const supplierName = (id: string | null) => (id ? suppliers.data?.items.find((s) => s.id === id)?.name || "—" : "—");

  const columns: TableColumn<PurchaseRow>[] = [
    { key: "date", header: t("fields.purchaseDate"), render: (p) => formatDate(p.date, lang) },
    { key: "supplier", header: t("fields.supplier"), render: (p) => supplierName(p.supplierId) },
    {
      key: "invoice",
      header: t("fields.invoice"),
      hideOnMobile: true,
      render: (p) => [p.invoice?.serie, p.invoice?.folio].filter(Boolean).join("-") || (p.invoice?.uuid ? p.invoice.uuid.slice(0, 8) : "—"),
    },
    { key: "items", header: t("fields.items"), align: "right", render: (p) => p.itemCount ?? 0 },
    ...(canSeeCosts
      ? [
          { key: "total", header: t("fields.total"), align: "right" as const, render: (p: PurchaseRow) => formatMoney(p.total ?? p.subtotal, p.currency, lang) },
          {
            key: "finance",
            header: t("fields.finance"),
            render: (p: PurchaseRow) => <Badge tone={toneOf(FINANCE_STATUS_TONES, p.finance?.status)}>{financeStatusLabel(p.finance?.status, lang)}</Badge>,
          },
        ]
      : []),
  ];

  return (
    <SectionFrame
      title={t("compras.title")}
      subtitle={t("compras.subtitle")}
      actions={
        <Button size="sm" icon={<i className="ri-add-line" />} onClick={() => setCreating(true)} data-testid="purchase-create">
          {t("compras.create")}
        </Button>
      }
      filters={
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <Select
            aria-label={t("fields.supplier")}
            value={supplierId}
            onChange={(e) => setSupplierId(e.target.value)}
            options={[{ value: "", label: t("filters.allSuppliers") }, ...(suppliers.data?.items || []).map((s) => ({ value: s.id, label: s.name }))]}
          />
        </div>
      }
    >
      {list.isLoading ? (
        <Loader />
      ) : list.isError ? (
        <ErrorState error={list.error} onRetry={() => void list.refetch()} />
      ) : rows.length === 0 ? (
        supplierId ? (
          <EmptyState icon="ri-filter-off-line" title={t("compras.noResults.title")} description={t("compras.noResults.text")} />
        ) : (
          <EmptyState
            icon="ri-file-list-3-line"
            title={t("compras.empty.title")}
            description={t("compras.empty.text")}
            action={
              <Button size="sm" icon={<i className="ri-add-line" />} onClick={() => setCreating(true)}>
                {t("compras.create")}
              </Button>
            }
          />
        )
      ) : (
        <>
          <Table columns={columns} rows={rows} rowKey={(p) => p.id} onRowClick={(p) => open(p.id)} />
          {list.hasNextPage ? (
            <div className="flex justify-center">
              <Button size="sm" variant="secondary" onClick={() => void list.fetchNextPage()} disabled={list.isFetchingNextPage}>
                {list.isFetchingNextPage ? <InlineSpinner /> : t("common.loadMore")}
              </Button>
            </div>
          ) : null}
        </>
      )}

      {creating ? <PurchaseFormModal canSeeCosts={canSeeCosts} onClose={() => setCreating(false)} onOpenPurchase={(id) => open(id)} /> : null}
      {editing ? <PurchaseFormModal purchase={editing} canSeeCosts={canSeeCosts} onClose={() => setEditing(null)} onOpenPurchase={(id) => open(id)} /> : null}
      {openId && !editing ? (
        <PurchaseDetailModal
          purchaseId={openId}
          canSeeCosts={canSeeCosts}
          onClose={() => open(null)}
          onEdit={(p) => setEditing(p)}
        />
      ) : null}
    </SectionFrame>
  );
}
